import type { ConceptDef } from './types';
import { FIND_CHOICES, MODEL_CHOICES, PICTURE_CHOICES, PLACE_CHOICES, ROOM_CHOICES, type SceneChoice } from '../games/scenes/catalog';

/*
 * WHAT EACH KIND OF GAME ACTUALLY HAS, DECLARED IN ONE PLACE.
 *
 * The settings page used to offer "default tiles per round" and "default unique
 * words" as if every game were a grid of tiles. Of the hundred concepts, 57 are
 * tile matches and 43 are not: a supervisor-scored echoic trial has no tiles at
 * all — the adult taps a tick — and a sequence has no tile count because its
 * length is the content. Those settings were meaningless for nearly half the
 * app, and `errorless` (a wrong TILE not responding) was global for the same
 * reason and with the same fault.
 *
 * THE RULE THIS FILE EXISTS TO ENFORCE: adding a kind must not require a
 * migration, a settings-page change, or an editor change. A kind declares what
 * it has, and the forms, the defaults and the validation are all read off that
 * declaration. Anything hard-coded elsewhere is the bug this is replacing.
 *
 * SO NOTHING IS ASSUMED UNIVERSAL. Even `promptMode` — which three of the four
 * kinds share — is listed by each kind that has it, because `timerHold` does
 * not, and the moment one parameter is assumed to be everywhere the next kind
 * added is the one that breaks it.
 *
 * WHAT IS NOT HERE: how a thing looks. Palette, text size, spacing, motion,
 * voice and tile SIZE stay global, because they describe the learner rather
 * than the task. "How big are tiles" is a sensory preference; "how many tiles"
 * is a teaching decision. Different questions, different homes.
 */

export type ConceptKind = ConceptDef['kind'];

export type ParamShape =
  | { type: 'preset'; options: readonly { value: string; label: string }[]; fallback: string }
  | { type: 'count'; min: number; max: number; fallback: number }
  | { type: 'seconds'; min: number; max: number; fallback: number }
  | { type: 'toggle'; fallback: boolean };

export type ParamDef = {
  /** Stored under this name, in the kind's defaults and in a program's config. */
  id: string;
  label: string;
  hint?: string;
  programOnly?: boolean;
} & ParamShape;

/**
 * A rule between parameters, which a per-parameter declaration cannot express.
 *
 * "Unique words cannot exceed tiles per round" is a relationship, not a
 * property of either field. These were buried in a hand-written `validate` on
 * each template; if they are not declared here they are simply lost when the
 * forms become generated.
 */
export type Constraint = {
  /** Shown when it fails. Written for a supervisor, not a developer. */
  message: string;
  /** True when the combination is acceptable. */
  holds: (params: Record<string, ParamValue>, content: ContentValue) => boolean;
};

export type ParamValue = string | number | boolean;

/**
 * The part of a program that is not a setting.
 *
 * A word pool is not a parameter — it is the material being taught, it has no
 * sensible default, and editing it needs a purpose-built control rather than a
 * number box. Kinds that need one name it; kinds that do not, do not.
 */
/**
 * One thing to teach: what is said, and what answers it.
 *
 * `also` holds the other words that answer the same prompt. The board shows one of them at a
 * time and never two at once — two names for one thing side by side would make two tiles
 * right, which is not the discrimination the trial is teaching.
 */
export type ContentItem = { say: string; answer: string; also?: string[] };

/**
 * One thing to ask for, where nothing is tapped.
 *
 * `hint` is for the adult giving the prompt, never shown as an answer — there is
 * no answer on screen to give away.
 */
export type ContentPrompt = { prompt: string; hint?: string };

/**
 * One order to learn: what is asked for, and the items in the order they go.
 *
 * The items are stored in the right order and shown shuffled. Two rows may share
 * an item — Monday appears in more than one routine — because nothing here is a
 * board where two tiles could both be correct.
 */
export type ContentSequence = { label: string; items: string[] };

/**
 * The material a kind teaches with.
 *
 * TWO SIDES, NOT A WORD LIST. This began as a flat list of strings, which
 * assumed the prompt and the tile face were the same text. That is true of word
 * matching and false of most of the concepts: "something red" is answered by an
 * apple, and a colour by a coloured square. A flat list could not hold either.
 */
export type ContentSpec =
  | { type: 'none' }
  | {
      type: 'pairs';
      label: string;
      hint?: string;
      /** What the learner hears or reads. */
      sayLabel: string;
      /** What they tap. */
      answerLabel: string;
      /** What a brand-new program starts with, so it is playable immediately. */
      starter: readonly ContentItem[];
      maxItems: number;
      maxItemLength: number;
    }
  | {
      type: 'prompts';
      label: string;
      hint?: string;
      /** What the adult gives. */
      promptLabel: string;
      /** A note to the adult about what counts, shown beside the prompt. */
      hintLabel: string;
      starter: readonly ContentPrompt[];
      maxItems: number;
      maxItemLength: number;
    }
  | {
      type: 'sequences';
      label: string;
      hint?: string;
      /** What is asked for, which is the prompt. */
      sequenceLabel: string;
      /** The items, entered in the order they belong in. */
      itemsLabel: string;
      starter: readonly ContentSequence[];
      maxSequences: number;
      maxItems: number;
      maxItemLength: number;
    }
  | {
      type: 'pictureSentences';
      label: string;
      hint?: string;
      pictureLabel: string;
      sentencesLabel: string;
      starter: readonly string[];
      maxSentences: number;
      maxWords: number;
    }
  | {
      type: 'scene';
      label: string;
      hint?: string;
      partsLabel: string;
      scenes: readonly SceneChoice[];
      minParts: number;
      starterParts: number;
    }
  | { type: 'custom' };

export type ContentValue = {
  items?: ContentItem[];
  prompts?: ContentPrompt[];
  sequences?: ContentSequence[];
  scene?: string;
  parts?: string[];
  picture?: string;
  sentences?: string[];
} | null;

export type KindDef = {
  kind: ConceptKind;
  /** What a supervisor calls this kind of game. */
  label: string;
  /** What the learner actually does, in one line. */
  describes: string;
  /**
   * EMPTY IS A REAL ANSWER. A sequence and a supervisor-scored prompt have no
   * numeric parameters: their length is the list of items, which is content.
   * Offering a "default length" for them would invent a setting that cannot be
   * applied to anything.
   */
  params: readonly ParamDef[];
  /** The material this kind teaches with, if it has any. */
  content: ContentSpec;
  /** Rules between parameters, or between a parameter and the content. */
  constraints: readonly Constraint[];
  /** Not offered in pickers: programs of this kind are made elsewhere (Studio). */
  hidden?: boolean;
  developer?: boolean;
};

/* Shared shapes, defined once so two kinds cannot describe the same parameter
   differently — but still listed by each kind that has it, never inherited. */

const PROMPT_MODE: ParamDef = {
  id: 'promptMode',
  label: 'Prompt',
  hint: 'Whether the prompt is spoken or shown as text.',
  type: 'preset',
  fallback: 'AUDIO',
  options: [
    { value: 'AUDIO', label: 'Spoken' },
    { value: 'TEXT', label: 'Written' },
  ],
};

const PROMPT_REPEAT: ParamDef = {
  id: 'promptRepeatSeconds',
  label: 'Repeat the spoken prompt every (seconds)',
  hint: 'Leave empty to use the time set in Settings, under Appearance.',
  type: 'seconds',
  min: 5,
  max: 60,
  fallback: 5,
  programOnly: true,
};

const PROMPT_DELAY: ParamDef = {
  id: 'promptDelay',
  label: 'Repeat the prompt after',
  type: 'preset',
  fallback: 'short',
  options: [
    { value: 'immediate', label: 'Immediately' },
    { value: 'short', label: '2 seconds' },
    { value: 'long', label: '4 seconds' },
    { value: 'progressive', label: 'Progressive — stretches with accuracy' },
  ],
};

const REINFORCEMENT: ParamDef = {
  id: 'reinforcement',
  label: 'Confirm a correct answer',
  hint: 'Thinning confirmation is part of the teaching, not a saving.',
  type: 'preset',
  fallback: 'always',
  options: [
    { value: 'always', label: 'Every time' },
    { value: 'variable', label: 'Sometimes' },
  ],
};

const SESSION_LENGTH: ParamDef = {
  id: 'sessionLength',
  label: 'End the session after',
  type: 'preset',
  fallback: 'unlimited',
  options: [
    { value: 'unlimited', label: 'Until stopped' },
    { value: 'tenTrials', label: '10 trials' },
    { value: 'twentyTrials', label: '20 trials' },
    { value: 'fiveMinutes', label: '5 minutes' },
  ],
};

const MASTERY: ParamDef = {
  id: 'masteryCriterion',
  label: 'Count a target as learned after',
  type: 'preset',
  fallback: 'none',
  options: [
    { value: 'none', label: 'Do not track mastery' },
    { value: 'threeInARow', label: '3 correct in a row' },
    { value: 'fiveInARow', label: '5 correct in a row' },
    { value: 'eightyPercent', label: '80% of the last 10' },
  ],
};

const ERRORLESS: ParamDef = {
  id: 'errorless',
  label: 'Errorless teaching',
  hint: 'A wrong choice does not respond at all. Standard while something is being acquired.',
  type: 'preset',
  fallback: 'off',
  options: [
    { value: 'off', label: 'Off — a wrong choice goes back and is tried again' },
    { value: 'on', label: 'On — only the right place or answer responds' },
  ],
};

const CHIP_SPEECH: ParamDef = {
  id: 'chipSpeech',
  label: 'Say a card’s word when it is touched',
  type: 'toggle',
  fallback: true,
};

const CHIP_FACE: ParamDef = {
  id: 'chipFace',
  label: 'Cards show',
  type: 'preset',
  fallback: 'both',
  options: [
    { value: 'both', label: 'Picture and word' },
    { value: 'picture', label: 'Picture only' },
    { value: 'word', label: 'Word only' },
  ],
};

const sceneRule = (min: number): Constraint => ({
  message: `Tick at least ${min} ${min === 1 ? 'part' : 'parts'} of the picture.`,
  holds: (_p, c) => (c?.parts?.length ?? 0) >= min,
});

const rowsRule = (what: string): Constraint => ({
  message: `Add at least one ${what}.`,
  holds: (_p, c) => (c?.sequences?.length ?? 0) > 0,
});

const pairsRule = (what: string): Constraint => ({
  message: `Add at least one ${what}.`,
  holds: (_p, c) => (c?.items?.length ?? 0) > 0,
});

const TRIAL_ORDER: ParamDef = {
  id: 'trialOrder',
  label: 'Present targets',
  type: 'preset',
  fallback: 'random',
  options: [
    { value: 'random', label: 'In random order' },
    { value: 'fixed', label: 'In the order listed' },
    { value: 'interspersed', label: 'Mixed with mastered targets' },
  ],
};

export const KINDS: Record<ConceptKind, KindDef> = {
  tileMatch: {
    kind: 'tileMatch',
    label: 'Tile match',
    describes: 'The learner taps the tile that matches the prompt.',
    params: [
      PROMPT_MODE,
      {
        id: 'tileCount',
        label: 'Tiles on screen',
        hint: 'How many tiles the board shows, including the answer.',
        type: 'count',
        min: 2,
        max: 12,
        fallback: 4,
      },
      {
        id: 'uniqueWordCount',
        label: 'Different items per round',
        hint: 'How many distinct items are drawn from the pool for one round.',
        type: 'count',
        min: 1,
        max: 12,
        fallback: 4,
      },
      {
        id: 'errorless',
        label: 'Errorless teaching',
        hint: 'A tile that is not the answer does not respond at all. '
            + 'Standard while a target is being acquired, because a repeated error gets learned too.',
        type: 'preset',
        fallback: 'off',
        options: [
          { value: 'off', label: 'Off — a wrong tap repeats the prompt' },
          { value: 'on', label: 'On — only the right tile responds' },
        ],
      },
      {
        id: 'distractorSimilarity',
        label: 'Choose wrong answers that are',
        hint: 'The largest single difficulty lever.',
        type: 'preset',
        fallback: 'random',
        options: [
          { value: 'random', label: 'Unrelated — easiest' },
          { value: 'category', label: 'From the same category' },
          { value: 'nearMiss', label: 'Near-misses — hardest' },
        ],
      },
      {
        id: 'allowRepeatedTiles',
        label: 'Allow the same item on more than one tile',
        hint: 'Off by default: if the repeated item is the answer, two tiles are '
            + 'both correct and the discrimination being taught disappears.',
        type: 'toggle',
        fallback: false,
      },
      PROMPT_REPEAT,
      TRIAL_ORDER,
      MASTERY,
      REINFORCEMENT,
      SESSION_LENGTH,
    ],
    content: {
      type: 'pairs',
      label: 'What this program teaches',
      hint: 'Each row is one thing to learn: what the learner hears, and which tile answers it. '
          + 'For plain word matching the two are the same.',
      sayLabel: 'Prompt (heard or read)',
      answerLabel: 'Tile that answers it',
      starter: [
        { say: 'bed', answer: 'bed' },
        { say: 'dog', answer: 'dog' },
        { say: 'sun', answer: 'sun' },
        { say: 'pen', answer: 'pen' },
      ],
      maxItems: 200,
      maxItemLength: 60,
    },
    /* CARRIED OVER FROM THE HAND-WRITTEN validate(). Generating the form would
       otherwise drop these silently: a supervisor could ask for six unique
       words out of four tiles and only find out when the board could not be
       built. The first is enforced by the API as well. */
    constraints: [
      {
        message: 'Different items per round cannot exceed the number of tiles.',
        holds: (p) => Number(p.uniqueWordCount) <= Number(p.tileCount),
      },
      {
        message: 'There need to be at least as many rows as the different-items setting.',
        holds: (p, c) => (c?.items?.length ?? 0) >= Number(p.uniqueWordCount),
      },
      {
        message: 'Add at least one thing to teach.',
        holds: (_p, c) => (c?.items?.length ?? 0) > 0,
      },
      {
        /* DISTINCT BY WHAT IS TAPPED. Two rows may share a prompt — several
           things are "something red" — but two tiles with the same face would
           both be correct, which destroys the discrimination being taught. */
        message: 'Two rows answer with the same tile, which would make both correct.',
        holds: (_p, c) => {
          const faces = (c?.items ?? []).map((i) => i.answer.trim().toLowerCase()).filter(Boolean);
          return new Set(faces).size === faces.length;
        },
      },
    ],
  },

  supervisorScored: {
    kind: 'supervisorScored',
    label: 'Supervisor scored',
    describes: 'The prompt is given, the learner responds, and the adult marks it correct or not.',
    /* NO TILE PARAMETERS AND NO ERRORLESS. There is nothing on screen to tap:
       the adult scores what they heard. The number of prompts is the list of
       prompts, which is content rather than a setting. */
    params: [PROMPT_MODE, PROMPT_DELAY, TRIAL_ORDER, MASTERY, REINFORCEMENT, SESSION_LENGTH],
    content: {
      type: 'prompts',
      label: 'What this program teaches',
      hint: 'Each row is one thing to give. The adult gives the prompt, the learner responds, '
          + 'and the adult marks it.',
      promptLabel: 'Prompt (heard or read)',
      hintLabel: 'What counts as right',
      starter: [
        { prompt: 'I want it' },
        { prompt: 'open the door' },
        { prompt: 'my turn' },
        { prompt: 'all done' },
      ],
      maxItems: 200,
      maxItemLength: 200,
    },
    constraints: [
      {
        message: 'Add at least one prompt.',
        holds: (_p, c) => (c?.prompts?.length ?? 0) > 0,
      },
    ],
  },

  sequenceTap: {
    kind: 'sequenceTap',
    label: 'Sequence',
    describes: 'The learner taps items in the right order.',
    /* Length is the sequence itself — seven days are seven days. */
    params: [PROMPT_MODE, PROMPT_DELAY, MASTERY, REINFORCEMENT, SESSION_LENGTH],
    content: {
      type: 'sequences',
      label: 'What this program teaches',
      hint: 'Each row is one order to learn. Enter the items in the right order — they are '
          + 'shown shuffled, and the learner puts them back.',
      sequenceLabel: 'What is asked for',
      itemsLabel: 'The items, in order',
      starter: [
        { label: 'Days of the week.', items: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] },
      ],
      maxSequences: 60,
      maxItems: 24,
      maxItemLength: 60,
    },
    constraints: [
      {
        message: 'Add at least one order to learn.',
        holds: (_p, c) => (c?.sequences?.length ?? 0) > 0,
      },
      {
        /* TWO IS THE SMALLEST THING THAT HAS AN ORDER. A single item is tapped
           correctly whatever the learner does, which records a success nobody
           earned. */
        message: 'Every order needs at least two items.',
        holds: (_p, c) => (c?.sequences ?? []).every((s) => s.items.length >= 2),
      },
    ],
  },

  timerHold: {
    kind: 'timerHold',
    label: 'Timed hold',
    describes: 'A prompt is shown and a countdown runs.',
    /* NO PROMPT MODE, AND THAT IS THE POINT OF DECLARING RATHER THAN INHERITING.
       Three kinds out of four have one; this does not, so anything that assumed
       every game had a prompt mode would break here first. There is also no
       correctness to reinforce or master — the hold either lasts or it does
       not. */
    params: [
      {
        id: 'holdSeconds',
        label: 'Hold for',
        type: 'seconds',
        min: 1,
        max: 120,
        fallback: 5,
      },
      {
        id: 'rounds',
        label: 'Number of holds',
        type: 'count',
        min: 1,
        max: 20,
        fallback: 3,
      },
      SESSION_LENGTH,
    ],
    content: {
      type: 'prompts',
      label: 'What is held',
      hint: 'Each row is one thing to hold for the time set above. One is chosen for each go.',
      promptLabel: 'Prompt (shown and spoken)',
      hintLabel: 'What counts as holding it',
      starter: [{ prompt: 'Look at me' }],
      maxItems: 60,
      maxItemLength: 200,
    },
    constraints: [
      {
        message: 'Add at least one thing to hold.',
        holds: (_p, c) => (c?.prompts?.length ?? 0) > 0,
      },
    ],
  },

  labelParts: {
    kind: 'labelParts',
    label: 'Label the picture',
    describes: 'The learner drags each word card onto the right part of a picture — or dresses a person.',
    params: [
      CHIP_FACE,
      CHIP_SPEECH,
      {
        id: 'spotHint',
        label: 'Show where cards go',
        type: 'preset',
        fallback: 'dots',
        options: [
          { value: 'dots', label: 'A marked spot on each part' },
          { value: 'none', label: 'No marks — the learner finds the part' },
        ],
      },
      { id: 'extraCards', label: 'Extra cards that do not belong', type: 'count', min: 0, max: 3, fallback: 0 },
      ERRORLESS,
      MASTERY,
      REINFORCEMENT,
      SESSION_LENGTH,
    ],
    content: {
      type: 'scene',
      label: 'The picture and its parts',
      hint: 'Choose a picture, then tick the parts the learner labels. Start with two or three and add more as they are learned.',
      partsLabel: 'Parts to label',
      scenes: PICTURE_CHOICES,
      minParts: 1,
      starterParts: 3,
    },
    constraints: [sceneRule(1)],
  },

  findPart: {
    kind: 'findPart',
    label: 'Touch the part',
    describes: 'The learner hears “touch the chimney” and touches that part of the picture.',
    params: [PROMPT_MODE, ERRORLESS, PROMPT_DELAY, TRIAL_ORDER, MASTERY, REINFORCEMENT, SESSION_LENGTH],
    content: {
      type: 'scene',
      label: 'The picture and its parts',
      hint: 'Choose a picture, then tick the parts the learner is asked to find.',
      partsLabel: 'Parts to find',
      scenes: FIND_CHOICES,
      minParts: 1,
      starterParts: 4,
    },
    constraints: [sceneRule(1)],
  },

  findInScene: {
    kind: 'findInScene',
    label: 'Find it in the picture',
    describes: 'A picture of a place, such as a kitchen or a park. The learner hears “find the cup” and touches it.',
    params: [PROMPT_MODE, ERRORLESS, PROMPT_DELAY, TRIAL_ORDER, MASTERY, REINFORCEMENT, SESSION_LENGTH],
    content: {
      type: 'scene',
      label: 'The picture and what to find',
      hint: 'Choose a place, then tick the things the learner is asked to find.',
      partsLabel: 'Things to find',
      scenes: PLACE_CHOICES,
      minParts: 1,
      starterParts: 4,
    },
    constraints: [sceneRule(1)],
  },

  placeIt: {
    kind: 'placeIt',
    label: 'Put it where I say',
    describes: 'The learner moves an object to the place named — in, on, under, next to, behind.',
    params: [
      {
        id: 'mover',
        label: 'The object that is moved',
        type: 'preset',
        fallback: 'cat',
        options: [
          { value: 'cat', label: '🐱 cat' },
          { value: 'ball', label: '⚽ ball' },
          { value: 'teddy', label: '🧸 teddy' },
          { value: 'dog', label: '🐶 dog' },
          { value: 'apple', label: '🍎 apple' },
        ],
      },
      PROMPT_MODE,
      ERRORLESS,
      PROMPT_DELAY,
      MASTERY,
      REINFORCEMENT,
      SESSION_LENGTH,
    ],
    content: {
      type: 'scene',
      label: 'The places to use',
      hint: 'Tick the places the learner is asked to put the object.',
      partsLabel: 'Places',
      scenes: ROOM_CHOICES,
      minParts: 2,
      starterParts: 4,
    },
    constraints: [sceneRule(2)],
  },

  sortBins: {
    kind: 'sortBins',
    label: 'Sort into groups',
    describes: 'The learner drags each card into the group it belongs to.',
    params: [
      { id: 'itemsPerRound', label: 'Cards to sort in one go', type: 'count', min: 2, max: 12, fallback: 6 },
      CHIP_SPEECH,
      ERRORLESS,
      MASTERY,
      REINFORCEMENT,
      SESSION_LENGTH,
    ],
    content: {
      type: 'sequences',
      label: 'The groups',
      hint: 'Each row is one group: its name, then the things that belong in it. Two or three groups work best.',
      sequenceLabel: 'Group',
      itemsLabel: 'What belongs in it',
      starter: [
        { label: '🔥 hot', items: ['☀️ sun', '🔥 fire', '🍲 soup'] },
        { label: '🧊 cold', items: ['🧊 ice', '⛄ snowman', '🍦 ice cream'] },
      ],
      maxSequences: 4,
      maxItems: 20,
      maxItemLength: 60,
    },
    constraints: [
      rowsRule('group'),
      {
        message: 'Sorting needs at least two groups.',
        holds: (_p, c) => (c?.sequences?.length ?? 0) >= 2,
      },
    ],
  },

  yesNo: {
    kind: 'yesNo',
    label: 'Yes or no',
    describes: 'The learner answers a question with a big Yes or No.',
    params: [PROMPT_MODE, ERRORLESS, PROMPT_DELAY, TRIAL_ORDER, MASTERY, REINFORCEMENT, SESSION_LENGTH],
    content: {
      type: 'pairs',
      label: 'The questions',
      hint: 'Write the question (a picture can go at the start, e.g. “🐱 Is this a dog?”) and whether the answer is yes or no.',
      sayLabel: 'Question',
      answerLabel: 'Answer (yes or no)',
      starter: [
        { say: '🐶 Is this a dog?', answer: 'yes' },
        { say: '🐱 Is this a dog?', answer: 'no' },
        { say: '🍎 Is this an apple?', answer: 'yes' },
        { say: '🚗 Is this a bus?', answer: 'no' },
      ],
      maxItems: 200,
      maxItemLength: 120,
    },
    constraints: [
      pairsRule('question'),
      {
        message: 'Every answer must be yes or no.',
        holds: (_p, c) => (c?.items ?? []).every((i) => ['yes', 'no'].includes(i.answer.trim().toLowerCase())),
      },
    ],
  },

  oddOneOut: {
    kind: 'oddOneOut',
    label: 'Odd one out',
    describes: 'The learner taps the one that does not belong with the others.',
    params: [PROMPT_MODE, ERRORLESS, MASTERY, REINFORCEMENT, SESSION_LENGTH],
    content: {
      type: 'pairs',
      label: 'The groups',
      hint: 'List the ones that belong, separated by commas, then the odd one out.',
      sayLabel: 'The ones that belong (comma separated)',
      answerLabel: 'The odd one out',
      starter: [
        { say: '🍎 apple, 🍌 banana, 🍇 grapes', answer: '🐶 dog' },
        { say: '🚗 car, 🚌 bus, 🚲 bike', answer: '🍕 pizza' },
      ],
      maxItems: 100,
      maxItemLength: 160,
    },
    constraints: [
      pairsRule('group'),
      {
        message: 'Each group needs at least two that belong.',
        holds: (_p, c) => (c?.items ?? []).every((i) => i.say.split(',').filter((s) => s.trim()).length >= 2),
      },
    ],
  },

  findAll: {
    kind: 'findAll',
    label: 'Find them all',
    describes: 'The learner taps every picture that fits — “tap all the animals”.',
    params: [
      { id: 'boardSize', label: 'Pictures on the board', type: 'count', min: 4, max: 12, fallback: 9 },
      PROMPT_MODE,
      ERRORLESS,
      MASTERY,
      REINFORCEMENT,
      SESSION_LENGTH,
    ],
    content: {
      type: 'sequences',
      label: 'What to find',
      hint: 'Each row is one search: what is asked, then everything that fits. Pictures from the other rows fill the rest of the board.',
      sequenceLabel: 'What is asked',
      itemsLabel: 'Everything that fits',
      starter: [
        { label: 'Tap all the animals', items: ['🐶 dog', '🐱 cat', '🐮 cow', '🐷 pig'] },
        { label: 'Tap all the food', items: ['🍎 apple', '🍞 bread', '🧀 cheese', '🍌 banana'] },
        { label: 'Tap all the vehicles', items: ['🚗 car', '🚌 bus', '🚲 bike', '🚂 train'] },
      ],
      maxSequences: 40,
      maxItems: 12,
      maxItemLength: 60,
    },
    constraints: [rowsRule('search')],
  },

  memoryPairs: {
    kind: 'memoryPairs',
    label: 'Memory pairs',
    describes: 'The learner turns cards over two at a time to find the pairs.',
    params: [
      { id: 'pairsPerBoard', label: 'Pairs on the board', type: 'count', min: 2, max: 8, fallback: 4 },
      { id: 'faceUp', label: 'Show all cards face up (matching, no memory needed)', type: 'toggle', fallback: false },
      CHIP_SPEECH,
      REINFORCEMENT,
      SESSION_LENGTH,
    ],
    content: {
      type: 'pairs',
      label: 'The pairs',
      hint: 'Each row is one pair. Use the same thing twice for identical pairs, or two things that go together.',
      sayLabel: 'First card',
      answerLabel: 'Its partner',
      starter: [
        { say: '🐶 dog', answer: '🐶 dog' },
        { say: '🐱 cat', answer: '🐱 cat' },
        { say: '🐮 cow', answer: '🐮 cow' },
        { say: '🐷 pig', answer: '🐷 pig' },
      ],
      maxItems: 60,
      maxItemLength: 60,
    },
    constraints: [
      pairsRule('pair'),
      {
        message: 'Pairs on the board cannot be more than the pairs listed.',
        holds: (p, c) => (c?.items?.length ?? 0) >= Math.min(2, Number(p.pairsPerBoard)),
      },
    ],
  },

  slotOrder: {
    kind: 'slotOrder',
    label: 'Put them in order',
    describes: 'The learner drags cards into numbered spaces — words into a sentence, letters into a word, pictures into a story.',
    params: [PROMPT_MODE, CHIP_SPEECH, ERRORLESS, MASTERY, REINFORCEMENT, SESSION_LENGTH],
    content: {
      type: 'sequences',
      label: 'What is built',
      hint: 'Each row is one thing to build: what is asked, then the pieces in the right order.',
      sequenceLabel: 'What is asked',
      itemsLabel: 'The pieces, in order',
      starter: [
        { label: 'I want juice', items: ['I', 'want', 'juice'] },
        { label: 'The cat is sleeping', items: ['The', 'cat', 'is', 'sleeping'] },
      ],
      maxSequences: 60,
      maxItems: 10,
      maxItemLength: 40,
    },
    constraints: [
      rowsRule('thing to build'),
      { message: 'Every row needs at least two pieces.', holds: (_p, c) => (c?.sequences ?? []).every((s) => s.items.length >= 2) },
    ],
  },

  pictureSentence: {
    kind: 'pictureSentence',
    label: 'Build the sentence about a picture',
    describes: 'The learner looks at a picture and drags the mixed-up words into the right order to make a sentence about it.',
    params: [PROMPT_MODE, CHIP_SPEECH, ERRORLESS, MASTERY, REINFORCEMENT, SESSION_LENGTH],
    content: {
      type: 'pictureSentences',
      label: 'The picture and its sentences',
      hint: 'One sentence is shown at a time, in a random order. When it is built correctly, the next one comes.',
      pictureLabel: 'Picture',
      sentencesLabel: 'Sentences about the picture',
      starter: ['The cat is on the mat.', 'The cat is asleep.'],
      maxSentences: 20,
      maxWords: 10,
    },
    constraints: [
      { message: 'Add a picture.', holds: (_p, c) => !!c?.picture },
      { message: 'Add at least one sentence.', holds: (_p, c) => (c?.sentences?.length ?? 0) > 0 },
      { message: 'Every sentence needs between 2 and 10 words.', holds: (_p, c) => (c?.sentences ?? []).every((s) => { const n = s.trim().split(/\s+/).filter(Boolean).length; return n >= 2 && n <= 10; }) },
    ],
  },
  blockCopy: {
    kind: 'blockCopy',
    label: 'Copy the pattern',
    describes: 'The learner copies a pattern of coloured blocks onto an empty grid.',
    params: [
      {
        id: 'modelStays',
        label: 'The pattern to copy',
        type: 'preset',
        fallback: 'stays',
        options: [
          { value: 'stays', label: 'Stays on screen' },
          { value: 'hides', label: 'Hides after 5 seconds (from memory)' },
        ],
      },
      ERRORLESS,
      REINFORCEMENT,
      SESSION_LENGTH,
    ],
    content: {
      type: 'sequences',
      label: 'The patterns',
      hint: 'Each row is one pattern: a name, then the colour of every square, row by row (4 squares for 2×2, 9 for 3×3, 16 for 4×4). Colours: red, blue, yellow, green, orange, purple, white.',
      sequenceLabel: 'Pattern name',
      itemsLabel: 'Colours, row by row',
      starter: [
        { label: 'Two by two', items: ['red', 'blue', 'blue', 'red'] },
        { label: 'Stripes', items: ['yellow', 'yellow', 'yellow', 'blue', 'blue', 'blue', 'yellow', 'yellow', 'yellow'] },
      ],
      maxSequences: 40,
      maxItems: 16,
      maxItemLength: 10,
    },
    constraints: [
      rowsRule('pattern'),
      {
        message: 'Each pattern needs 4, 9 or 16 colours.',
        holds: (_p, c) => (c?.sequences ?? []).every((s) => [4, 9, 16].includes(s.items.length)),
      },
    ],
  },

  taskChain: {
    kind: 'taskChain',
    label: 'Step by step',
    describes: 'A daily task shown one step at a time; the adult records how much help each step needed.',
    params: [PROMPT_MODE, MASTERY, SESSION_LENGTH],
    content: {
      type: 'sequences',
      label: 'The tasks',
      hint: 'Each row is one task: its name, then every step in order.',
      sequenceLabel: 'Task',
      itemsLabel: 'The steps, in order',
      starter: [
        { label: 'Wash hands', items: ['🚰 Turn on the tap', '💧 Wet hands', '🧼 Use soap', '👐 Rub hands', '💦 Rinse', '🚰 Turn off the tap', '🧻 Dry hands'] },
      ],
      maxSequences: 30,
      maxItems: 20,
      maxItemLength: 80,
    },
    constraints: [rowsRule('task')],
  },

  waitForGo: {
    kind: 'waitForGo',
    label: 'Wait for go',
    describes: 'The learner waits until the button turns green, then taps it.',
    params: [
      { id: 'minWait', label: 'Shortest wait', type: 'seconds', min: 1, max: 30, fallback: 2 },
      { id: 'maxWait', label: 'Longest wait', type: 'seconds', min: 1, max: 60, fallback: 6 },
      { id: 'rounds', label: 'Turns', type: 'count', min: 1, max: 20, fallback: 5 },
      REINFORCEMENT,
      SESSION_LENGTH,
    ],
    content: {
      type: 'prompts',
      label: 'What is said while waiting',
      hint: 'One is chosen for each turn.',
      promptLabel: 'Message',
      hintLabel: 'Note for the adult',
      starter: [{ prompt: 'Wait for green' }],
      maxItems: 20,
      maxItemLength: 120,
    },
    constraints: [
      { message: 'The longest wait must be at least the shortest.', holds: (p) => Number(p.maxWait) >= Number(p.minWait) },
    ],
  },

  traceIt: {
    kind: 'traceIt',
    label: 'Trace or write it',
    describes: 'The learner traces or writes a letter, number, word or shape with a finger or pen.',
    params: [
      {
        id: 'guide',
        label: 'Help shown',
        type: 'preset',
        fallback: 'trace',
        options: [
          { value: 'trace', label: 'Trace over a clear outline' },
          { value: 'faint', label: 'Trace over a faint outline' },
          { value: 'copy', label: 'Copy it into an empty space' },
        ],
      },
      {
        id: 'strictness',
        label: 'How close it must be',
        type: 'preset',
        fallback: 'normal',
        options: [
          { value: 'gentle', label: 'Roughly' },
          { value: 'normal', label: 'Fairly close' },
          { value: 'strict', label: 'Close' },
        ],
      },
      PROMPT_MODE, ERRORLESS, TRIAL_ORDER, MASTERY, REINFORCEMENT, SESSION_LENGTH,
    ],
    content: {
      type: 'prompts',
      label: 'What to trace or write',
      hint: 'One per row: a letter, a number, a short word, or a shape (circle, square, triangle, line, cross, zigzag, wave).',
      promptLabel: 'Letter, number, word or shape',
      hintLabel: 'Note for you (not shown)',
      starter: [{ prompt: 'a' }, { prompt: 'b' }, { prompt: 'circle' }],
      maxItems: 60,
      maxItemLength: 12,
    },
    constraints: [
      { message: 'Add at least one thing to trace or write.', holds: (_p, c) => (c?.prompts ?? []).some((p) => p.prompt.trim().length > 0) },
    ],
  },

  studio: {
    kind: 'studio',
    label: 'Studio game',
    describes: 'A game type made in Studio. What the learner does is described by the type itself.',
    params: [PROMPT_MODE, ERRORLESS, MASTERY, REINFORCEMENT, SESSION_LENGTH],
    content: { type: 'none' },
    constraints: [],
    hidden: true,
  },

  rotateFind: {
    kind: 'rotateFind',
    label: 'Turn it and find (3D)',
    describes: 'A 3D object the learner can turn around to find the part that is asked for.',
    params: [
      {
        id: 'startView',
        label: 'Start from',
        type: 'preset',
        fallback: 'front',
        options: [
          { value: 'front', label: 'The front' },
          { value: 'random', label: 'A random side' },
        ],
      },
      PROMPT_MODE,
      ERRORLESS,
      MASTERY,
      REINFORCEMENT,
      SESSION_LENGTH,
    ],
    content: {
      type: 'scene',
      label: 'The object and its parts',
      hint: 'Choose an object, then tick the parts to find. Parts on the far side teach turning it round.',
      partsLabel: 'Parts to find',
      scenes: MODEL_CHOICES,
      minParts: 1,
      starterParts: 4,
    },
    constraints: [sceneRule(1)],
  },
};

/**
 * Every rule this kind breaks, given what a supervisor has entered.
 *
 * Returns messages rather than a boolean so the form can show all of them at
 * once instead of revealing one per attempt.
 */
export function violations(
  kind: ConceptKind,
  params: Record<string, ParamValue>,
  content: ContentValue,
): string[] {
  return KINDS[kind].constraints.filter((c) => !c.holds(params, content)).map((c) => c.message);
}

const listKinds = (developer: boolean) => (Object.keys(KINDS) as ConceptKind[]).filter((k) => !KINDS[k].hidden && !!KINDS[k].developer === developer);

export let ALL_KINDS: readonly ConceptKind[] = listKinds(false);
export let DEVELOPER_KINDS: readonly ConceptKind[] = listKinds(true);

export function registerKind(def: KindDef) {
  (KINDS as Record<string, KindDef>)[def.kind] = def;
  ALL_KINDS = listKinds(false);
  DEVELOPER_KINDS = listKinds(true);
}

/** Every parameter value a kind starts with, before any default or override. */
export function kindFallbacks(kind: ConceptKind): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const p of KINDS[kind].params) out[p.id] = p.fallback;
  return out;
}

/**
 * What a program actually runs with.
 *
 * THREE LAYERS, RESOLVED IN ONE PLACE so the editor, the settings page and the
 * game cannot disagree about what "unset" means: the kind's own fallback, then
 * the supervisor's default for that kind, then whatever the program states.
 *
 * KEYS THE KIND DOES NOT DECLARE ARE DROPPED. A program that once carried a
 * tile count and is now some other kind must not quietly keep applying it.
 */
export function resolveParams(
  kind: ConceptKind,
  supervisorDefaults: Record<string, unknown> | null | undefined,
  programConfig: Record<string, unknown> | null | undefined,
): Record<string, string | number | boolean> {
  const declared = new Set(KINDS[kind].params.map((p) => p.id));
  const out = kindFallbacks(kind);
  for (const source of [supervisorDefaults, programConfig]) {
    if (!source) continue;
    for (const [key, value] of Object.entries(source)) {
      if (!declared.has(key)) continue;
      if (value === null || value === undefined) continue;
      out[key] = value as string | number | boolean;
    }
  }
  return out;
}
